import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import findByIdsFactory from 'scoreboard/charts/model/find-by-ids-factory'
import Dataset from 'scoreboard/datasets/model/dataset'

const findByIds = findByIdsFactory()

// Creates a new DashboardData object. Valid options include:
//  * data: initial data to use, instead of fetching it
//  * fetchOptions: function that returns valid $.ajax() options
//    to fetch fresh data
var DashboardData = function (opt) {
	opt = opt || {}

	this._data = tweakData(opt.data || {})
	this._parentData = opt.parentData
	this.fetchOptions = opt.fetchOptions

	this.datasetModels = {}
}

_.extend(DashboardData.prototype, Backbone.Events)

// Fetches fresh domain object data, firing a 'change' event
// when finished. Will call fetchOptions (must be implemented)
// to get options for the $.ajax() call
DashboardData.prototype.fetch = function (opt) {
	if (this.disabledFetch) {
		return new Promise((resolve, reject) => {
			resolve()
		})
	}
	opt = opt || {}

	this.trigger('fetching')

	// Abort any in-process fetches
	_.result(this._fetcher, 'abort')

	this._fetcher = new Fetcher({
		dashboardData: this,
		fetchOptions: opt
	})

	return this._fetcher.promise()
}

// Returns the current data
DashboardData.prototype.get = function () {
	return _.cloneDeep(this._data)
}

/**
 * Manually sets the data, saving a fetch call
 *
 * @param {Object|DashboardData} data - either the raw Object that comes from
 * the server XHR or another DashboardData instance
 */
DashboardData.prototype.set = function (data = {}) {
	if (data instanceof DashboardData) {
		data = data._data
	}

	this._data = data._tweaked ? data : tweakData(data)

	data._shared = true

	this.trigger('change')
}

/**
 * Before a getOrSetSectionData() call, clones the underlying _data if necessary
 */
DashboardData.prototype.cloneDataIfNecessary = function (section, getOrSetArgs) {
	if (!_.includes(['scorecards', 'initiatives'])) {
		// Only scorecards and initiatives contain period-specific data in
		// DashboardData that could cause conflicts between different widgets;
		// any other type of object is safe to share, so we don't need to
		// clone it
		return
	}

	if (!this._data._shared) {
		// If the data isn't shared between multiple widgets, we definitely
		// don't need to clone it
		return
	}

	if (getOrSetArgs.length < 2) {
		// Don't need to clone if we're only reading the data (0 or 1 arguments)
		return
	}

	// If the underlying data object is shared with other widgets, we need to
	// clone it, or we could wipe out state the other objects were using
	// (like period-specific scores)
	this._data = _.cloneDeep(this._data)
	delete this._data.shared
}

var sections = ['scorecards', 'initiatives', 'reports', 'strategyMaps', 'dashboards', 'briefings', 'files',
	'organizations', 'datasetTreeItems', 'datasetFieldSuggestions', 'initiativeTrees', 'forms', 'charts']

// Creates methods like dashboardData.scorecards(id) and DashboardData.scorecards(data, id) for each section
sections.forEach(s => {
	// retrieves or sets the domain object data for this section and the given id
	DashboardData.prototype[s] = function (id, sn) {
		this.cloneDataIfNecessary(s, arguments)
		return getOrSetSectionData.bind(null, this._data, s).apply(null, arguments)
	}

	// retrieves or sets the domain object data in the given POJO data object for this section and the given id
	DashboardData[s] = function (data, id, sn) {
		return getOrSetSectionData.bind(null, data, s).apply(null, Array.prototype.slice.call(arguments, 1))
	}
})

DashboardData.prototype.reportMetadata = function (type, id, meta) {
	var subtypeData = getOrSetSectionData(this._data, 'reportMetadata', type)

	if (_.isUndefined(meta)) {
		return subtypeData ? subtypeData[id] : null
	}

	subtypeData[id] = meta
}

DashboardData.prototype.chartDesignersById = function (id) {
	return _.find(this._data.chartDesigners, { id: parseInt(id) })
}

DashboardData.prototype.chartDesignersByIds = function ({ id, tempId }) {
	return findByIds({ id, tempId, collection: this._data.chartDesigners })
}

DashboardData.prototype.tablesById = function (id) {
	return _.find(this._data.tables, { id: parseInt(id) })
}

/**
 * Returns the ChartData (in plain Object form) for the given chart designer ID.
 * Accepts id or tempId to account for persisted vs unpersisted charts
 *
 * @param {Int} [id] ID of the chart designer (if persisted)
 * @param {String} [tempId] tempId of the chart designer (if not persisted)
 *
 * @returns {Object} the chart data (can be used in constructor for ChartData)
 */
DashboardData.prototype.chartDataByIds = function ({ id, tempId }) {
	let dataAndIds = findByIds({ id, tempId, collection: this._data.chartData })
	return _.get(dataAndIds, 'chartData')
}

/**
 * If only dashboard data and a chart series are passed in, will return the aggregated
 * value (see AggregatedSeriesValue.kt) that was calculated for that chart series
 * (uses series id or cid as necesssary).
 *
 * This is currently only used for multi-period pie charts.
 *
 * If a value is also provided, will set the aggregated value for that series.
 */
DashboardData.aggregatedValuesBySeries = function (data, s, value) {
	if (arguments.length === 2) {
		if (!data) {
			return []
		}

		if (s.id) {
			return _.find(data.aggregatedSeriesValues, { seriesId: parseInt(s.id) })
		} else if (s.cid) {
			return _.find(data.aggregatedSeriesValues, { seriesCid: s.cid })
		} else {
			return []
		}
	} else {
		if (!data.aggregatedSeriesValues) {
			data.aggregatedSeriesValues = []
		}

		data.aggregatedSeriesValues.push(_.extend({
			seriesId: s.id,
			seriesCid: s.cid
		}, value))
	}
}

DashboardData.prototype.aggregatedValuesBySeries = function () {
	return DashboardData.aggregatedValuesBySeries.bind(null, this._data).apply(null, arguments)
}

DashboardData.prototype.datasetTreeItemsById = function () {
	return _.keyBy(this._data.datasetTreeItems, 'id')
}

/**
 * Adds the given array of dataset tree items to the data
 */
DashboardData.prototype.addDatasetTreeItems = function (treeItems) {
	_.forEach(treeItems, item => this.datasetTreeItems(item.id, item))
}

DashboardData.prototype.disableFetch = function () {
	this.disabledFetch = true
}

DashboardData.prototype.enableFetch = function () {
	this.disabledFetch = false
}

DashboardData.datasets = function (data, id, ds) {
	return getOrSetSectionData.bind(null, data, 'datasets').apply(null, Array.prototype.slice.call(arguments, 1))
}

DashboardData.prototype.datasets = function (id, ds) {
	// If setting a new dataset object, clear out the cached Dataset model
	if (!_.isUndefined(ds)) {
		delete this.datasetModels[id]
	}

	this.cloneDataIfNecessary('datasets', arguments)
	return getOrSetSectionData.bind(null, this._data, 'datasets').apply(null, arguments)
}

/**
 * Sets or returns a Dataset model for the given dataset ID. This keeps an
 * internal cache because the Dataset model can be expensive to build for
 * datasets with lots of fields and links
 */
DashboardData.prototype.datasetModel = function (id, model) {
	// set the model, if appropriate
	if (!_.isNil(model)) {
		if (model instanceof Dataset) {
			this.datasetModels[id] = model
		} else {
			// if a Dataset model is needed down the line, it will be created
			// lazily for optimal performance. So do NOT set the model here.
			// (instantiating Datasets can be an expensive operation)
			this.datasets(id, model)
		}
		return
	}

	// #57550 See if the parentData (ie the dashboard-level data) already has it
	// first, so we only create one model of a dataset per dashboard
	model = this._parentData?.datasetModel(id) ?? this.datasetModels[id]
	if (model) {
		return model
	}

	let ds = this.datasets(id)

	if (ds) {
		model = new Dataset(ds)
		this.datasetModels[id] = model
		return model
	} else {
		return null
	}
}

function getOrSetSectionData (data, section, id, domainObject) {
	// If didn't provide an ID, asking for all section data
	if (arguments.length === 2) {
		return data[section]
	}

	// If provided an ID but no domainObject, asking for a specific object's data
	if (arguments.length === 3) {
		return data[section] ? data[section][id] : null
	}

	// If provided a domain object, we're adding the domain object to the data
	if (!data[section]) {
		data[section] = {}
	}

	data[section][id] = domainObject
}

// Does some client-side tweaking of the data from the server,
// such as arranging the array of scores into a hash by periodId
function tweakData (data) {
	if (!data) {
		return
	}

	_.forEach(data.scorecards, function (sn) {
		sn.scores = _.keyBy(sn.scores, 'periodId')
	})

	_.forEach(data.initiatives, function (init) {
		init.calculations = _.keyBy(init.calculations, 'periodId')
	})

	data._tweaked = true

	return data
}

/**
 * Performs a fetch request for DashboardData. Handles things like:
 *  - deferring the fetch in case multiple changes are made to the dashboard at
 *    once, so we only make a single fetch (issue #32533)
 *  - aborting the XHR if one is in progress when another fetch request is made
 *  - providing a Promise for use by callers that triggered the fetch
 */
class Fetcher {
	constructor ({ dashboardData, fetchOptions }) {
		this._dashboardData = dashboardData
		this._promise = new Promise((resolve, reject) => {
			this._resolve = resolve
			this._reject = reject
		})

		// Using a timeout of 0 to allow the event loop to complete, in case
		// multiple change events would trigger another fetch request
		this._timeout = setTimeout(() =>
			this._doFetch(dashboardData.fetchOptions(fetchOptions)), 0)
	}

	promise () {
		return this._promise
	}

	_doFetch (fetchOptions) {
		this._ajaxPromise = $.ajax(fetchOptions)
			.done(function (data, textStatus, jqXHR) {
				this._dashboardData.set(data || {})
				this._resolve.apply(this._resolve, arguments)
			}.bind(this))
	}

	abort () {
		clearTimeout(this._timeout)
		_.result(this._ajaxPromise, 'abort')
		// Considered rejecting this._promise using this._reject, but that ends
		// up dumping an uncaught promise error to the console, and this isn't
		// really an error scenario we want to flag. So for now, we'll just
		// leave the promise unresolved
	}
}

export default DashboardData

DashboardData.__filename__ = 'dashboards/dashboard-data.js'
